import * as React from 'react';
import { RouteComponentProps } from 'react-router';
import { Control, Errors, Form } from 'react-redux-form';

// Models
import { CustomerSearchViewModel, CustomerViewModel } from '@models/Customer';
import { ContractViewModel, ContractOrderType } from '@models/Contract';
import { PageViewModel } from '@models/common.models';
import { Customer } from '../../../customer';

// Form Validation
import { getDefaultErrorMessages } from '@common/validators';

// Components
import { TypeAheadComponent, ModalProps } from '@common/components';
import { Option } from 'react-select';
import { EditPermissions, EditableFields } from '@models/EditPermissions';

export interface CustomerInfoProperties extends RouteComponentProps<{ id?: number }> {
  readonly message: string;
  readonly currentStep: number;
  readonly buttonRef: (button: HTMLButtonElement) => void;
  readonly onSaveClick: (model: ContractViewModel) => void;
  readonly customerLookup: (query: string, id?: number) => Promise<PageViewModel<CustomerViewModel>>;
  readonly onBuyerCustomerSelect: (model: string, value: number) => void;
  readonly buyerCustomer?: CustomerViewModel;
  readonly onSellerCustomerSelect: (model: string, value: number) => void;
  readonly sellerCustomer?: CustomerViewModel;
  readonly toggleModal: (modalProps?: ModalProps) => void;
  readonly clearBuyer: () => void;
  readonly clearSeller: () => void;
  readonly sellerCoverage: boolean;
  readonly isOrderEdit: boolean;
  readonly isOpenContract: boolean;

  readonly orderType?: ContractOrderType;
}

export class CustomerInfoComponent extends React.Component<CustomerInfoProperties> {
  checkCustomer = (values: ContractViewModel) => {
    var hasBuyer = values.buyerId || this.props.buyerCustomer ? true : false;

    // NOTE: THIS VALIDATION IS CURRENTLY N/A BUT LEAVING CODE IF NEEDED IN FUTURE
    // return true;

    if (this.props.orderType === ContractOrderType.DTC) {
      return hasBuyer;
    }

    return true;
  };

  shouldComponentUpdate(nextProps: Readonly<CustomerInfoProperties>, nextState: CustomerInfoProperties) {
    if (
      !this.props ||
      !nextProps ||
      this.props.buyerCustomer !== nextProps.buyerCustomer ||
      this.props.sellerCustomer !== nextProps.sellerCustomer ||
      this.props.currentStep !== nextProps.currentStep ||
      this.props.sellerCoverage !== nextProps.sellerCoverage
    ) {
      return true;
    }

    return false;
  }

  componentDidUpdate(prevProps: Readonly<CustomerInfoProperties>): void {
    if (
      prevProps.buyerCustomer !== this.props.buyerCustomer ||
      prevProps.sellerCustomer !== this.props.sellerCustomer
    ) {
      // Added this to fix an IE bug. Forced deselect of typeahead component after picking something in the dropdown.
      // Lookup WISE-328 for context
      document.body.focus();
    }
  }

  showBuyerCustomerModal = () => {
    if (this.props.buyerCustomer) {
      this.props.toggleModal({
        title: 'Edit Buyer/Owner Customer',
        component: Customer,
        saveCallback: this.loadBuyerOwner.bind(this),
        childProps: { entityId: this.props.buyerCustomer.id }
      });
    } else {
      this.props.toggleModal({
        title: 'Add New Buyer/Owner Customer',
        component: Customer,
        saveCallback: this.loadBuyerOwner.bind(this)
      });
    }
  };

  loadBuyerOwner(id: number) {
    this.props.toggleModal();
    this.props.onBuyerCustomerSelect('', id);
  }

  showSellerCustomerModal = () => {
    if (this.props.sellerCustomer) {
      this.props.toggleModal({
        title: 'Edit Seller Customer',
        component: Customer,
        saveCallback: this.loadSellerOwner.bind(this),
        childProps: { entityId: this.props.sellerCustomer.id }
      });
    } else {
      this.props.toggleModal({
        title: 'Add New Seller Customer',
        component: Customer,
        saveCallback: this.loadSellerOwner.bind(this)
      });
    }
  };

  loadSellerOwner(id: number) {
    this.props.toggleModal();
    this.props.onSellerCustomerSelect('', id);
  }

  renderError() {
    if (!this.props.message) {
      return;
    }

    return <div className="error">{this.props.message}</div>;
  }

  renderCustomerSearch(suggestion: Option<CustomerSearchViewModel>) {
    return (
      <div>
        <div>Name: {suggestion.name ? suggestion.name : '—'}</div>
        <div>Phone #: {suggestion.phone ? suggestion.phone : '—'}</div>
        <div>Email: {suggestion.emailAddress ? suggestion.emailAddress : '-'}</div>
      </div>
    );
  }

  renderCustomer(customer?: CustomerViewModel) {
    if (!customer) {
      return;
    }

    return (
      <div className="col-md-12 mb-10">
        <div className="row mt-10 mb-10 pl-15">
          <div className="col-md-3">{customer.name}</div>
          <div className="col-md-3">Phone: {customer.phone}</div>
          <div className="col-md-5 ellipsis">Email: {customer.emailAddress}</div>
        </div>
      </div>
    );
  }

  renderAddEditBuyer() {
    if (!this.props.buyerCustomer) {
      return (
        <button
          className="btn btn-primary mt-5"
          type="button"
          disabled={
            this.props.isOrderEdit && !EditPermissions.CanEditField(EditableFields.Buyer, this.props.isOpenContract)
          }
          onClick={this.showBuyerCustomerModal}
        >
          Add New Customer
        </button>
      );
    }
    return (
      <div className="row">
        <div className="col-md-1 mt-15">
          {this.props.isOrderEdit ? (
            EditPermissions.CanEditField(EditableFields.Buyer, this.props.isOpenContract) && (
              <i className="fa fa-times" onClick={this.props.clearBuyer} />
            )
          ) : (
            <i className="fa fa-times" onClick={this.props.clearBuyer} />
          )}
        </div>
        <div className="col-md-2 mt-5">
          <button
            className="btn btn-primary"
            type="button"
            disabled={
              this.props.isOrderEdit && !EditPermissions.CanEditField(EditableFields.Buyer, this.props.isOpenContract)
            }
            onClick={this.showBuyerCustomerModal}
          >
            Edit Customer
          </button>
        </div>
      </div>
    );
  }

  renderAddEditSeller() {
    if (!this.props.sellerCustomer) {
      return (
        <button
          className="btn btn-primary mt-5"
          type="button"
          disabled={
            this.props.isOrderEdit && !EditPermissions.CanEditField(EditableFields.Seller, this.props.isOpenContract)
          }
          onClick={this.showSellerCustomerModal}
        >
          Add New Customer
        </button>
      );
    }
    return (
      <div className="row">
        <div className="col-md-1 mt-15">
          {this.props.isOrderEdit ? (
            EditPermissions.CanEditField(EditableFields.Seller) && (
              <i className="fa fa-times" onClick={this.props.clearSeller} />
            )
          ) : (
            <i className="fa fa-times" onClick={this.props.clearSeller} />
          )}
        </div>
        <div className="col-md-2 mt-5">
          <button
            className="btn btn-primary"
            type="button"
            onClick={this.showSellerCustomerModal}
            disabled={
              this.props.isOrderEdit && !EditPermissions.CanEditField(EditableFields.Seller, this.props.isOpenContract)
            }
          >
            Edit Customer
          </button>
        </div>
      </div>
    );
  }

  renderBuyerOwner() {
    return (
      <div>
        <div className="row dark-gray-bc">
          <div className="col-md-12">
            <h3>Buyer/Owner Customer</h3>
          </div>
          <div className="col-md-9 mb-10">
            <Control.text
              model=".buyerId"
              className="form-control"
              changeAction={this.props.onBuyerCustomerSelect}
              component={(props: { value: string; onChange: Function }) => {
                return (
                  <TypeAheadComponent
                    {...props}
                    onChange={props.onChange}
                    renderValue={this.renderCustomerSearch}
                    valueKey={'id'}
                    labelKey={'name'}
                    getSuggestions={this.props.customerLookup}
                    value={props.value}
                    disabled={
                      this.props.isOrderEdit &&
                      !EditPermissions.CanEditField(EditableFields.Buyer, this.props.isOpenContract)
                    }
                  />
                );
              }}
            />
            <Control.text className="txt-hidden" model=".buyerId" />
          </div>
          <div className="col-md-3 text-center">{this.renderAddEditBuyer()}</div>
        </div>
        <div className="row dark-gray-bc">{this.renderCustomer(this.props.buyerCustomer)}</div>
      </div>
    );
  }

  renderSeller() {
    if (this.props.orderType === ContractOrderType.DTC) {
      return;
    }
    return (
      <div className="pt-10">
        <div className="row dark-gray-bc">
          <div className="col-md-12">
            <h3>Seller Customer</h3>
          </div>
          <div className="col-md-9 mb-10">
            <Control.text
              model=".sellerId"
              className="form-control"
              changeAction={this.props.onSellerCustomerSelect}
              component={(props: { value: string; onChange: Function }) => {
                return (
                  <TypeAheadComponent
                    {...props}
                    onChange={props.onChange}
                    renderValue={this.renderCustomerSearch}
                    valueKey={'id'}
                    labelKey={'name'}
                    getSuggestions={this.props.customerLookup}
                    value={props.value}
                    disabled={
                      this.props.isOrderEdit &&
                      !EditPermissions.CanEditField(EditableFields.Seller, this.props.isOpenContract)
                    }
                  />
                );
              }}
            />
            <Control.text className="txt-hidden" model=".sellerId" />
          </div>
          <div className="col-md-3 text-center">{this.renderAddEditSeller()}</div>
        </div>
        <div className="row dark-gray-bc">{this.renderCustomer(this.props.sellerCustomer)}</div>
      </div>
    );
  }

  render() {
    return (
      <>
        <Form
          model="customerInfoForm"
          className="form-horizontal"
          hideNativeErrors={true}
          onSubmit={this.props.onSaveClick}
          validators={{ '': { hasCustomer: this.checkCustomer } }}
        >
          <div className="row">
            <div className="col-md-12 mt-3">
              <h2>Step {this.props.currentStep + 1}: Customer Information</h2>
            </div>
          </div>
          {this.renderBuyerOwner()}
          {this.renderSeller()}

          <div className="col-md-1">
            <button className="btn btn-hidden" type="submit" ref={this.props.buttonRef}>
              Save
            </button>
          </div>
          <div className="col-md-11">
            <Errors
              className="error"
              model="customerInfoForm"
              show={{ touched: true, focus: false }}
              messages={getDefaultErrorMessages({ hasCustomer: this.getErrorMessage() })}
            />
          </div>
        </Form>
      </>
    );
  }

  private getErrorMessage() {
    if (this.props.orderType === ContractOrderType.DTC) {
      return 'Please select a buyer';
    }

    return true;
  }
}



// WEBPACK FOOTER //
// ./src/contract/_components/customer-info/customer-info.component.tsx